(() => {
  const SELECTOR = '[data-bh-hero-showcase]';

  const headerParts = () => {
    const mobileBar = document.querySelector('.bh-mobile-header__bar');
    if (window.matchMedia('(max-width: 749px)').matches && mobileBar) {
      return { inner: mobileBar, container: mobileBar };
    }

    /*
     * Siempre prioriza el wrapper exterior de Shopify. Element.closest() no
     * respeta el orden de una lista de selectores y antes podía devolver el
     * sticky-header interior, dejando fuera el margen de la sección.
     */
    const container = document.querySelector('.section-header') ||
      [...document.querySelectorAll('.shopify-section-group-header-group')]
        .find((section) => section.querySelector('sticky-header, .header-wrapper')) ||
      document.querySelector('.bh-sticky-header');

    const inner = container?.querySelector('sticky-header, .header-wrapper') ||
      document.querySelector('sticky-header, .header-wrapper');

    if (!inner || !container) return { inner: null, container: null };
    return { inner, container };
  };

  const syncScrollState = () => {
    const top = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0);
    document.documentElement.classList.toggle('bh-hero-page-scrolled', top > 24);
  };

  const syncUnderHeader = () => {
    const roots = [...document.querySelectorAll(SELECTOR)];
    const enabledRoots = roots.filter((root) => root.dataset.underHeader === 'true');
    const enabled = enabledRoots.length > 0;
    const { inner, container } = headerParts();

    document.documentElement.classList.toggle('bh-hero-under-header', enabled);
    document.querySelectorAll('.bh-header-over-hero').forEach((header) => {
      if (header !== container) header.classList.remove('bh-header-over-hero');
    });
    container?.classList.toggle('bh-header-over-hero', enabled);
    roots.forEach((root) => {
      const sectionWrapper = root.closest('.section-bh-hero-showcase') || root.parentElement;
      if (root.dataset.underHeader !== 'true' || !container || !sectionWrapper) {
        root.style.removeProperty('margin-top');
        sectionWrapper?.style.removeProperty('margin-top');
        root.style.removeProperty('--bh-hero-header-overlap');
        sectionWrapper?.style.removeProperty('--bh-hero-header-height');
        return;
      }

      root.style.removeProperty('margin-top');
      sectionWrapper.style.removeProperty('margin-top');

      const pageTop = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0);
      if (pageTop > 24 && sectionWrapper.style.getPropertyValue('--bh-hero-header-height')) return;

      /*
       * Corrige contra los píxeles que realmente se ven en pantalla. Esto incluye
       * altura del header, márgenes, tracks del grid del body y cualquier espacio
       * agregado por Dawn o por el editor de Shopify.
       */
      const viewport = root.querySelector('[data-bh-showcase-viewport]') || root;
      const headerTop = container.getBoundingClientRect().top;
      const viewportTop = viewport.getBoundingClientRect().top;
      const renderedMarginTop = Number.parseFloat(
        window.getComputedStyle(sectionWrapper).marginTop
      ) || 0;
      const currentOverlap = Math.max(0, -renderedMarginTop);
      const visibleGap = viewportTop - headerTop;
      const overlap = Math.max(0, Math.round(currentOverlap + visibleGap));

      sectionWrapper.style.setProperty('--bh-hero-header-height', `${overlap}px`);
      root.style.setProperty('--bh-hero-header-overlap', `${overlap}px`);

      /* Segunda medición después de que el navegador aplique el nuevo margen. */
      window.requestAnimationFrame(() => {
        if (!root.isConnected || !sectionWrapper.isConnected) return;
        const remainingGap = viewport.getBoundingClientRect().top -
          container.getBoundingClientRect().top;
        if (Math.abs(remainingGap) < 1) return;
        const settledOverlap = Math.max(0, Math.round(overlap + remainingGap));
        sectionWrapper.style.setProperty('--bh-hero-header-height', `${settledOverlap}px`);
        root.style.setProperty('--bh-hero-header-overlap', `${settledOverlap}px`);
      });
    });
    syncScrollState();
  };

  class BHHeroShowcase {
    constructor(root) {
      this.root = root;
      /* Compatibilidad: elimina la franja si quedó impresa por un Liquid anterior. */
      root.querySelectorAll('.bh-hero-showcase__promo').forEach((promo) => promo.remove());
      this.slides = [...root.querySelectorAll('[data-bh-showcase-slide]')];
      this.dots = [...root.querySelectorAll('[data-bh-showcase-dot]')];
      this.viewport = root.querySelector('[data-bh-showcase-viewport]');
      this.previous = root.querySelector('[data-bh-showcase-prev]');
      this.next = root.querySelector('[data-bh-showcase-next]');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      /* Con varias piezas, el único comportamiento disponible es el feed vertical. */
      this.scrollStory = this.slides.length > 1;
      this.root.classList.toggle('bh-hero-showcase--scroll-story', this.scrollStory);
      this.touchStartY = null;
      this.touchStartedAtStart = false;
      this.touchStartedAtEnd = false;
      this.edgeReleaseTimer = 0;
      this.feedEngaged = false;
      this.lastPageY = Math.max(0, window.scrollY || 0);
      this.pageDirection = 0;
      this.controller = new AbortController();
      this.signal = this.controller.signal;

      this.bind();
      if (this.scrollStory && this.slides.length > 1) {
        this.root.classList.add('bh-hero-showcase--scroll-ready');
        this.prepareVerticalStory();
      } else {
        this.root.classList.remove('bh-hero-showcase--scroll-ready');
        this.show(this.index);
      }
    }

    bind() {
      this.previous?.addEventListener('click', () => {
        this.scrollToIndex(this.index - 1);
      }, { signal: this.signal });
      this.next?.addEventListener('click', () => {
        this.scrollToIndex(this.index + 1);
      }, { signal: this.signal });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.bhShowcaseDot);
          this.scrollToIndex(index);
        }, { signal: this.signal });
      });

      if (this.scrollStory) {
        window.addEventListener('scroll', () => this.handlePageScroll(), {
          passive: true,
          signal: this.signal
        });
        this.viewport?.addEventListener('scroll', () => this.queueScrollStoryUpdate(), {
          passive: true,
          signal: this.signal
        });
        this.viewport?.addEventListener('wheel', (event) => this.handleEdgeWheel(event), {
          passive: false,
          signal: this.signal
        });
        this.viewport?.addEventListener('touchstart', (event) => this.handleEdgeTouchStart(event), {
          passive: true,
          signal: this.signal
        });
        this.viewport?.addEventListener('touchend', (event) => this.handleEdgeTouchEnd(event), {
          passive: true,
          signal: this.signal
        });
        window.addEventListener('resize', () => this.handleStoryResize(), {
          passive: true,
          signal: this.signal
        });
      }

      this.viewport?.addEventListener('keydown', (event) => {
        if (!this.scrollStory) return;

        const actions = {
          ArrowUp: this.index - 1,
          PageUp: this.index - 1,
          ArrowDown: this.index + 1,
          PageDown: this.index + 1,
          Home: 0,
          End: this.slides.length - 1
        };
        if (!(event.key in actions)) return;
        event.preventDefault();
        this.scrollToIndex(actions[event.key]);
      }, { signal: this.signal });

      document.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest?.('[data-bh-showcase-slide]');
        if (!slide || !this.root.contains(slide)) return;
        const index = Number(slide.dataset.slideIndex);
        if (this.scrollStory) this.scrollToIndex(index);
        else this.show(index);
      }, { signal: this.signal });
    }

    prepareVerticalStory() {
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', 'false');
        slide.toggleAttribute('aria-current', active);
        if ('inert' in slide) slide.inert = false;
      });
      this.updateStoryControls();
      this.observeFeedEntry();
      window.requestAnimationFrame(() => this.queueScrollStoryUpdate());
    }

    handlePageScroll() {
      const nextY = Math.max(0, window.scrollY || document.documentElement.scrollTop || 0);
      const delta = nextY - this.lastPageY;
      if (Math.abs(delta) > 1) this.pageDirection = delta > 0 ? 1 : -1;
      this.lastPageY = nextY;
      this.queueScrollStoryUpdate();
    }

    observeFeedEntry() {
      if (!('IntersectionObserver' in window)) {
        this.feedEngaged = true;
        return;
      }

      this.entryObserver = new IntersectionObserver(([entry]) => {
        if (entry.intersectionRatio < .15) {
          this.feedEngaged = false;
          return;
        }
        if (entry.intersectionRatio < .55 || this.feedEngaged || this.edgeReleaseTimer) return;
        this.engageFeed(this.pageDirection);
      }, { threshold: [0, .15, .55, .8] });
      this.entryObserver.observe(this.root);
    }

    engageFeed(direction) {
      if (this.feedEngaged || this.edgeReleaseTimer) return;
      this.feedEngaged = true;
      const entryIndex = direction < 0 ? this.slides.length - 1 : direction > 0 ? 0 : this.index;
      this.index = Math.max(0, Math.min(this.slides.length - 1, entryIndex));
      this.viewport?.scrollTo({ top: this.index * this.storyPanelHeight(), behavior: 'auto' });
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', 'false');
        slide.toggleAttribute('aria-current', active);
      });
      this.updateStoryControls();
      this.root.classList.add('is-feed-visible');
      this.root.scrollIntoView({
        behavior: this.reducedMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    }

    storyPanelHeight() {
      return Math.max(1, this.viewport?.clientHeight || window.innerHeight);
    }

    storyEdges() {
      const top = this.viewport?.scrollTop || 0;
      const end = Math.max(0, (this.viewport?.scrollHeight || 0) - this.storyPanelHeight());
      return {
        atStart: top <= 3,
        atEnd: top >= end - 3
      };
    }

    queueScrollStoryUpdate() {
      if (!this.scrollStory || this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = 0;
        const viewportHeight = Math.max(1, window.innerHeight);
        const rootRect = this.root.getBoundingClientRect();
        const feedVisible = this.feedEngaged && !this.edgeReleaseTimer &&
          rootRect.top < viewportHeight * .8 && rootRect.bottom > viewportHeight * .2;
        this.root.classList.toggle('is-feed-visible', feedVisible);
        const nextIndex = Math.max(0, Math.min(
          this.slides.length - 1,
          Math.round((this.viewport?.scrollTop || 0) / this.storyPanelHeight())
        ));
        if (nextIndex === this.index) return;
        this.index = nextIndex;
        this.slides.forEach((slide, index) => {
          const active = index === this.index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', 'false');
          slide.toggleAttribute('aria-current', active);
          if ('inert' in slide) slide.inert = false;
        });
        this.updateStoryControls();
      });
    }

    updateStoryControls() {
      this.dots.forEach((dot, index) => {
        const active = index === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      if (this.previous) this.previous.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index === this.slides.length - 1;
    }

    scrollToIndex(index) {
      if (!this.scrollStory) return;
      const safeIndex = Math.max(0, Math.min(this.slides.length - 1, index));
      this.viewport?.scrollTo({
        top: safeIndex * this.storyPanelHeight(),
        behavior: this.reducedMotion ? 'auto' : 'smooth'
      });
    }

    handleEdgeWheel(event) {
      const delta = event.deltaY;
      if (Math.abs(delta) < 2) return;
      const { atStart, atEnd } = this.storyEdges();
      const direction = delta > 0 ? 1 : -1;
      if ((direction > 0 && !atEnd) || (direction < 0 && !atStart)) return;
      event.preventDefault();
      this.releaseFromFeed(direction);
    }

    handleEdgeTouchStart(event) {
      const touch = event.changedTouches[0];
      if (!touch) return;
      const { atStart, atEnd } = this.storyEdges();
      this.touchStartY = touch.clientY;
      this.touchStartedAtStart = atStart;
      this.touchStartedAtEnd = atEnd;
    }

    handleEdgeTouchEnd(event) {
      const touch = event.changedTouches[0];
      if (!touch || this.touchStartY === null) return;
      const delta = this.touchStartY - touch.clientY;
      this.touchStartY = null;
      if (Math.abs(delta) < 42) return;
      if (delta > 0 && this.touchStartedAtEnd) this.releaseFromFeed(1);
      if (delta < 0 && this.touchStartedAtStart) this.releaseFromFeed(-1);
    }

    releaseFromFeed(direction) {
      if (this.edgeReleaseTimer) return;
      const wrapper = this.root.closest('.shopify-section, .section-bh-hero-showcase') || this.root;
      const target = direction > 0 ? wrapper.nextElementSibling : wrapper.previousElementSibling;
      this.feedEngaged = false;
      this.root.classList.remove('is-feed-visible');
      this.edgeReleaseTimer = window.setTimeout(() => {
        this.edgeReleaseTimer = 0;
      }, 700);

      if (target) {
        target.scrollIntoView({
          behavior: this.reducedMotion ? 'auto' : 'smooth',
          block: direction > 0 ? 'start' : 'end'
        });
        return;
      }

      window.scrollBy({
        top: direction * Math.max(240, window.innerHeight * .72),
        behavior: this.reducedMotion ? 'auto' : 'smooth'
      });
    }

    handleStoryResize() {
      if (!this.scrollStory || this.resizeFrame) return;
      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = 0;
        this.viewport?.scrollTo({ top: this.index * this.storyPanelHeight(), behavior: 'auto' });
        this.queueScrollStoryUpdate();
      });
    }

    show(nextIndex) {
      if (this.slides.length < 1) return;
      this.index = (nextIndex + this.slides.length) % this.slides.length;

      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if ('inert' in slide) slide.inert = !active;
      });

      this.dots.forEach((dot, index) => {
        const active = index === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

    }

    destroy() {
      this.controller.abort();
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
      if (this.edgeReleaseTimer) window.clearTimeout(this.edgeReleaseTimer);
      this.entryObserver?.disconnect();
      this.root.classList.remove('bh-hero-showcase--scroll-ready');
      this.root.classList.remove('is-feed-visible');
      delete this.root.bhHeroShowcase;
    }
  }

  const mount = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      root.bhHeroShowcase?.destroy();
      root.bhHeroShowcase = new BHHeroShowcase(root);
    });
    syncUnderHeader();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTOR).forEach((root) => root.bhHeroShowcase?.destroy());
    window.requestAnimationFrame(syncUnderHeader);
  });

  window.addEventListener('resize', () => window.requestAnimationFrame(syncUnderHeader), { passive: true });
  window.addEventListener('load', syncUnderHeader, { once: true });
  window.addEventListener('scroll', syncScrollState, { passive: true });
})();

